import { z } from "zod";
import { pluginConnectionHealthStateSchema } from "./plugin-connection-health.js";
import { workerCredentialResponseSchema, workerPluginSecretReferenceSchema } from "./worker-credentials.js";
/*
 * The connection-health loop a worker runs for one tenant. The tenant owns the probe state and
 * every credential: the worker claims the next connection due, probes it with the credentials the
 * tenant releases for that connection only while the claim's lease lasts, and records the result
 * under the same lease. The worker holds no database or secret-store login.
 */
const id = z.string().trim().min(1).max(256);
const tenantSlug = z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/u);
const leaseToken = z.string().uuid();
/** A claimed connection, as the tenant's health subjects describe it. */
export const workerConnectionHealthTargetSchema = z.object({
    subject_id: id,
    tenant_slug: tenantSlug,
    organisation_id: id,
    user_context_id: id.nullable(),
    plugin_slug: id,
    adapter_slug: id,
    source_kind: id,
    secret_name: id.nullable(),
    credential_revision: z.string().min(1).max(64),
    lease_token: leaseToken,
}).strict();
/** The claim a probe's credential requests and its result are made under, at the revision it claimed. */
const probe = z.object({ subjectId: id, leaseToken, credentialRevision: z.string().min(1).max(64) }).strict();
export const workerConnectionHealthRequestSchema = z.discriminatedUnion("operation", [
    /*
     * Records the worker's heartbeat, runs the attention-notification pass when asked (within one
     * organisation and recipient when scoped), and claims the next connection due.
     */
    z.object({
        tenantSlug, operation: z.literal("claim"),
        notifications: z.object({
            scope: z.object({ organisationId: id, recipientUserId: id }).strict().optional(),
        }).strict().optional(),
    }).strict(),
    z.object({
        tenantSlug, operation: z.literal("record"), probe,
        result: z.object({
            state: pluginConnectionHealthStateSchema.exclude(["stale"]),
            operation: z.string().min(1).max(200).nullable(),
            errorCode: z.string().regex(/^[a-zA-Z0-9_.:-]{1,128}$/u).nullable(),
        }).strict(),
    }).strict(),
    /** A credential the claimed connection's fixed probe reads with, and nothing else. */
    z.object({
        tenantSlug, operation: z.literal("credential"), probe,
        request: z.discriminatedUnion("operation", [
            z.object({ operation: z.literal("plugin.resolve"), reference: workerPluginSecretReferenceSchema }).strict(),
            z.object({ operation: z.literal("plugin.app"), providerType: z.literal("native-oauth"), providerSlug: id }).strict(),
            z.object({
                operation: z.literal("connector.access"), provider: z.literal("xero"), organisationId: id,
                expectedIntegrationId: id.optional(),
            }).strict(),
            z.object({ operation: z.literal("google.connector") }).strict(),
        ]),
    }).strict(),
]);
/** A Google connector's OAuth client and refresh token, as its probe signs in with them. */
export const workerGoogleConnectorCredentialSchema = z.object({
    operation: z.literal("google.connector"),
    credential: z.object({ clientId: z.string().min(1), clientSecret: z.string().min(1), refreshToken: z.string().min(1) }).strict(),
}).strict();
export const workerConnectionHealthResponseSchema = z.discriminatedUnion("operation", [
    z.object({ operation: z.literal("claim"), target: workerConnectionHealthTargetSchema.nullable() }).strict(),
    z.object({ operation: z.literal("record"), recorded: z.boolean() }).strict(),
    z.object({
        operation: z.literal("credential"),
        result: z.union([workerCredentialResponseSchema, workerGoogleConnectorCredentialSchema]),
    }).strict(),
]);
export const WORKER_CONNECTION_HEALTH_REFUSALS = {
    probeLeaseNotHeld: "health_probe_lease_not_held",
};
